import { useEffect, useState } from 'react';

import { type ErrorLike } from '@apollo/client';
import { useQuery } from '@apollo/client/react';
import { Button, Stack } from '@mui/material';
import { Home, Plus } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';

import { gql } from '@graphql/__generated__';
import { type GetBuyerLeadsCardQuery } from '@graphql/__generated__/graphql';
import { CountBadge } from '@modules/_shared/components/CountBadge';
import { ListingAccordion } from '@modules/_shared/components/ListingAccordion';
import { MutationErrorSnackbar } from '@modules/_shared/components/MutationErrorModal';
import { formatAddress } from '@modules/_shared/utils/formatting';
import { BuyerLeadCreateDrawer } from '@modules/users/user-overview/components/BuyerLeadCreateDrawer';
import { LeadCardRow } from '@modules/users/user-overview/components/LeadCardRow';
import { useLocale } from '@shared/hooks/locale';

const GET_BUYER_LEADS_CARD = gql(/* GraphQL */ `
  query GetBuyerLeadsCard($userId: uuid!) {
    buyer_leads(
      where: { user_id: { _eq: $userId } }
      order_by: { created_at: desc }
      limit: 100
    ) {
      id
      created_at
      lead_stage {
        id
        label
      }
      lot {
        id
        property {
          street_number
          route
          postcode
          locality
          state
          country
        }
      }
    }
    buyer_leads_aggregate(where: { user_id: { _eq: $userId } }) {
      aggregate {
        count
      }
    }
    lead_stages(
      where: {
        pipeline: { lead_type: { _eq: "buyer" }, name: { _eq: "house_sales" } }
      }
      order_by: { order_nr: asc }
    ) {
      id
      name
      label
      status
    }
  }
`);

type BuyerLead = NonNullable<GetBuyerLeadsCardQuery['buyer_leads'][number]>;

const BuyerLeadsListItem = ({ buyerLead }: { buyerLead: BuyerLead }) => {
  const { t } = useLocale();
  const [searchParams] = useSearchParams();
  const address = buyerLead.lot?.property
    ? formatAddress(buyerLead.lot.property, ', ')
    : null;

  return (
    <LeadCardRow
      icon={<Home size={18} />}
      color="warning"
      date={buyerLead.created_at}
      to={{
        pathname: `./buyers/${buyerLead.id}`,
        search: searchParams.toString(),
      }}
      title={address || t('No address')}
      subtitle={buyerLead.lead_stage?.label}
    />
  );
};

type UserBuyerLeadsCardProps = {
  userId: string;
};

export const UserBuyerLeadsCard = ({ userId }: UserBuyerLeadsCardProps) => {
  const { t } = useLocale();
  const [apolloError, setApolloError] = useState<ErrorLike | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const { data, error, refetch } = useQuery(GET_BUYER_LEADS_CARD, {
    variables: { userId },
  });

  useEffect(() => {
    if (error) {
      setApolloError(error);
    }
  }, [error]);

  const buyerLeads = data?.buyer_leads ?? [];
  const count = data?.buyer_leads_aggregate?.aggregate?.count ?? 0;

  return (
    <>
      <ListingAccordion
        title={
          <>
            {t('Buyer leads')}
            {count > 0 && <CountBadge count={count} />}
          </>
        }
      >
        <Stack sx={{ gap: 0.5 }}>
          {buyerLeads.map(buyerLead => (
            <BuyerLeadsListItem key={buyerLead.id} buyerLead={buyerLead} />
          ))}
          <Button
            fullWidth
            variant="soft"
            color="primary"
            startIcon={<Plus />}
            onClick={() => setDrawerOpen(true)}
            sx={{ mt: buyerLeads.length > 0 ? 1 : 0 }}
          >
            {t('Add buyer lead')}
          </Button>
        </Stack>
      </ListingAccordion>

      <MutationErrorSnackbar
        error={apolloError}
        onClose={() => setApolloError(null)}
      />

      {drawerOpen && (
        <BuyerLeadCreateDrawer
          userId={userId}
          stages={data?.lead_stages ?? []}
          onClose={() => setDrawerOpen(false)}
          onCreate={() => refetch()}
        />
      )}
    </>
  );
};
